<script setup>
import GenericCard from "@/components/ui/card/GenericCard.vue";

import Switch from "@/components/ui/switch/Switch.vue";
import { ref, computed, watch } from "vue";
import { Check } from "lucide-vue-next";
import { useLocalization } from "@/composables/localization";
import { useConfig } from "@/composables/config";
import Button from "@/components/ui/button/Button.vue";
import GenericAccordion from "@/components/ui/accordion/GenericAccordion.vue";

const { numberToMoney } = useLocalization();
const { standardSubscriptionMonthlyPrice, standardSubscriptionAnnualPrice } = useConfig();

const props = defineProps({
    loading: {
        type: Boolean,
        default: false
    },
    yearly: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['select:plan'])

const planName = 'Standard'
const planSlug = 'standard'
const planDescription = 'I manage one market or trading group and want to connect buyers and suppliers there.'

const isYearly = ref(false);

const features = computed(() => {
    const paidPrice = isYearly.value ? standardSubscriptionAnnualPrice : standardSubscriptionMonthlyPrice;
    return [
        {
            type: 'Free',
            price: 0,
            description: 'Good for trying things out or running a small pilot market.',
            items: [
                'Set up one market',
                'Add buyers and sellers',
                'List products and enable trading',
                'Track basic transactions',
            ]
        },
        {
            type: 'Paid',
            price: numberToMoney(paidPrice, '', 0),
            description: 'For markets that want more control and a branded experience.',
            items: [
                'Brand your market (logo and identity)',
                'Assign market admins and staff',
                'Set and collect market fees',
                'View market performance and trends',
                'Optional member verification'
            ]
        }
    ]
})

const plan = computed(() => {
    return {
        yearly: isYearly.value,
        name: planSlug,
    }
})

const selectPlan = () => {
    emit('select:plan', plan.value);
}

watch(() => props.yearly, (newYearly) => {
    isYearly.value = newYearly;
}, { immediate: true })
</script>

<template>
    <div class="w-full max-w-2xl mx-auto">
        <GenericCard>
            <template #header>
                <div class="flex flex-col justify-center items-center">
                    <div class="text-center text-2xl font-medium text-primary-font">{{ planName }}</div>
                    <div class="flex w-full lg:w-sm justify-center text-center text-xs text-primary-gray">{{ planDescription }}</div>
                </div>
            </template>
            <template #content>
                <div>
                    <!-- Web View -->
                    <div class="hidden lg:flex justify-center items-center gap-4">
                        <div class="text-xs font-medium" :class="{ 'text-primary-font': !isYearly, 'text-primary-gray': isYearly }">Monthly</div>
                        <Switch v-model="isYearly" class="data-[state=checked]:bg-green-600/20">
                            <template #thumb>
                                <span class="pointer-events-none block h-4 w-4 rounded-full bg-primary-green shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"></span>
                            </template>
                        </Switch>   
                        <div class="text-xs font-medium" :class="{ 'text-primary-font': isYearly, 'text-primary-gray': !isYearly }">Yearly</div>
                    </div>
                    <div class="hidden lg:flex flex-col lg:flex-row justify-between items-start mt-6 gap-6">
                        <div v-for="feature in features" :key="feature.type" class="flex flex-col justify-between">
                            <div class="flex flex-row justify-between items-center">
                                <div class="text-sm font-medium text-primary-font">
                                    {{ feature.type }}
                                </div>
                                <div v-if="feature.price" class="text-base text-primary-green">
                                    {{ feature.price }}
                                </div>
                            </div>
                            <div class="text-xs text-primary-gray my-4">
                                {{ feature.description }}
                            </div>
                            <div class="flex flex-col bg-neutral-50 rounded-lg p-4">
                                <div v-if="feature.type === 'Paid'" class="flex flex-row items-center gap-2 py-2">
                                    <span class="text-xs text-primary-font">
                                        Everything in Free, plus
                                    </span>
                                </div>
                                <div v-for="item in feature.items" :key="item" class="flex flex-row items-center gap-2 py-2">
                                    <div>
                                        <Check class="text-white bg-green-500 rounded-full p-0.5" size="16" />
                                    </div>
                                    <span class="text-xs text-primary-gray">
                                        {{ item }}
                                    </span>
                              </div>
                          </div>
                        </div>
                    </div>
                    <!-- Mobile View -->
                     <div class="flex flex-col lg:hidden">
                        <GenericAccordion :title="feature.type" :value="feature.type" v-for="feature in features" :key="feature.type">
                            <template #title>
                                <div class="flex flex-col justify-start items-start">
                                    <div class="text-sm font-medium text-primary-font mb-2">
                                        {{ feature.type }}
                                    </div>
                                    <div v-if="feature.price" class="text-base text-primary-green mb-2 flex items-center gap-2">
                                        <div class="">
                                            <span>{{ feature.price }}</span>
                                            <span v-if="isYearly" class="text-xs text-primary-gray font-medium">/year</span>
                                            <span v-else class="text-xs text-primary-gray font-medium">/month</span>
                                        </div>
                                        <Switch v-model="isYearly" @click.stop class="data-[state=checked]:bg-green-600/20 h-4 w-9">
                                            <template #thumb>
                                                <span class="pointer-events-none block h-4 w-4 rounded-full bg-primary-green shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"></span>
                                            </template>
                                        </Switch>   
                                    </div>
                                    <div class="text-xs text-primary-gray text-start mb-2">
                                        {{ feature.description }}
                                    </div>
                                </div>
                            </template>
                            <template #default>
                                <div class="flex flex-col bg-neutral-50 rounded-lg p-4">
                                    <div v-if="feature.type === 'Paid'" class="flex flex-row items-center gap-2 py-2">
                                        <span class="text-xs text-primary-font">
                                            Everything in Free, plus
                                        </span>
                                    </div>
                                    <div v-for="item in feature.items" :key="item" class="flex flex-row items-center gap-2 py-2">
                                        <div>
                                            <Check class="text-white bg-green-500 rounded-full p-0.5" size="16" />
                                        </div>
                                        <span class="text-xs text-primary-gray">
                                            {{ item }}
                                        </span>
                                  </div>
                              </div>
                            </template>
                        </GenericAccordion>
                     </div>
                </div>
            </template>
          <template #footer>
            <div class="w-full">
                <div class="flex flex-col justify-center items-center">
                    <div class="text-sm text-primary-gray">Free and paid options are available.</div>
                    <div class="mt-2 w-full">
                        <Button 
                            variant="outline" 
                            :disabled="loading"
                            class="w-full shadow-none border border-primary-border font-semibold text-sm" 
                            @click="selectPlan"
                        >
                            {{loading ? 'Processing...' : 'Get Started'}}
                        </Button>
                    </div>
                </div>
            </div>
          </template>
        </GenericCard>
    </div>
</template>